import {
  ApolloCache,
  FetchResult,
  NormalizedCacheObject,
  useMutation,
} from '@apollo/client';
import { useToast } from '@synoptic/ui-kit/toast/toast-provider.js';
import {
  AddTournamentPostMutation,
  TournamentPostInput,
} from '@/__generated__/graphql.ts';
import { gql } from '@/__generated__/index.ts';
import { updateFeedCache } from '@/apollo-cache-utils/update-feed-cache.ts';
import { TournamentPostCardFragment } from '@/components/tournament-post-card/tournament-post-card-fragment.ts';
import { useMe } from '@/routes/_app/use-me.ts';

const ADD_TOURNAMENT_POST = gql(`
  mutation AddTournamentPost($input: TournamentPostInput!) {
    addTournamentPost(input: $input) {
      ...TournamentPostCardFragment
    }
  }
`);

const writeTournamentPostFragment = (
  cache: ApolloCache<NormalizedCacheObject>,
  addPost: NonNullable<
    FetchResult<AddTournamentPostMutation>['data']
  >['addTournamentPost'],
) => {
  return cache.writeFragment({
    fragment: TournamentPostCardFragment,
    fragmentName: 'TournamentPostCardFragment',
    data: addPost,
  });
};

export const useAddTournamentPost = (tournamentId: string) => {
  const me = useMe();
  const toast = useToast();

  const [addTournamentPostMutation, result] = useMutation(ADD_TOURNAMENT_POST, {
    onError(error) {
      console.error(error.message);
      toast.error({
        title: 'Failed to make a post',
        subTitle: 'Please try again',
      });
    },
    onCompleted() {
      toast.info({
        title: 'Your post was sent',
      });
    },
    update(cache, result) {
      const addPost = result.data?.addTournamentPost;
      if (addPost) {
        cache.modify({
          id: `User:${me.id}`,
          fields: {
            posts(postsPage) {
              const newPostRef = writeTournamentPostFragment(cache, addPost);
              return updateFeedCache(postsPage, newPostRef);
            },
          },
        });
        cache.modify({
          id: `Tournament:${tournamentId}`,
          fields: {
            mySubmissions(page) {
              const newPostRef = writeTournamentPostFragment(cache, addPost);
              return updateFeedCache({ items: page }, newPostRef).items;
            },
            submissions(page) {
              const newPostRef = writeTournamentPostFragment(cache, addPost);
              return updateFeedCache(page, newPostRef);
            },
          },
        });
      }
    },
  });

  const addTournamentPost = (input: TournamentPostInput) =>
    addTournamentPostMutation({
      variables: {
        input,
      },
    });

  return [addTournamentPost, result] as const;
};
